import { copyTextWithNotify } from "@/helpers/clipboard.helper";
import { ImageUploadModal } from "@/components/avatar-upload/ImageUploadModal.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import {
  canCreateEmployeeInviteLink,
  canSendEmployeeInvite,
  getEmployeeInviteEmail,
  getEmployeeInviteEmailTypes,
} from "@/pages/employees/employee/components/employee-invite.types.ts";
import { EmployeeCompensationEditModal } from "@/pages/employees/employee/widgets/compensation/EmployeeCompensationEditModal.tsx";
import { EmployeeDeleteModal } from "@/pages/employees/employee/widgets/EmployeeDeleteModal.tsx";
import { EmployeeEmploymentEditModal } from "@/pages/employees/employee/widgets/EmployeeEmploymentEditModal.tsx";
import { EmployeeInviteEmailModal } from "@/pages/employees/employee/widgets/EmployeeInviteEmailModal.tsx";
import { EmployeePortalAccessModal } from "@/pages/employees/employee/widgets/EmployeePortalAccessModal.tsx";
import { EmployeePositionEditModal } from "@/pages/employees/employee/widgets/EmployeePositionEditModal.tsx";
import { EmployeeTerminationEditModal } from "@/pages/employees/employee/widgets/EmployeeTerminationEditModal.tsx";
import { EmployeeEditModal } from "@/pages/employees/widgets/EmployeeEditModal.tsx";
import { WorkflowAssignToEmployeeModal } from "@/pages/employees/widgets/WorkflowAssignToEmployeeModal.tsx";
import {
  useEmployeeCreateInviteLink,
  useEmployeeDeleteAvatar,
  useEmployeeDeleteBackground,
  useEmployeeMe,
  useEmployeeSendInviteConfirmEmail,
  useEmployeeSetAvatar,
  useEmployeeSetBackground,
  useEmployeeTwoFactorDisableForce,
  useEmployeeUpdateBan,
  useImpersonationStart,
} from "@/queries/employees/use-employees.query.ts";
import { useEmployeePortalAccess } from "@/queries/preboarding/use-preboarding.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiEmployee, PortalAccessMode } from "@/types/employees/employee.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { ApiPermission, EmployeeFieldPermission, PermissionType } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { ApiInviteEmailType } from "@/types/workflows/workflow.action-notification.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSub,
  FontAwesomeIcon,
  useModals,
  useToasts,
} from "@skailer-com/uikit";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export const EmployeeActionsMenu = ({ employee }: { employee: ApiEmployee }) => {
  const { t } = useTranslation();
  const { canViewField, canEditField, canEditAnyOfFields, canIEdit, isFeatureEnabled } =
    usePermissions();
  const { openModal } = useModals();
  const { showInfo } = useToasts();
  const navigate = useNavigate();
  const { data: employeeMe } = useEmployeeMe();

  const employeeSetBackground = useEmployeeSetBackground();
  const employeeDeleteBackground = useEmployeeDeleteBackground();
  const employeeDisableTwoFactor = useEmployeeTwoFactorDisableForce();
  const employeeSetAvatar = useEmployeeSetAvatar();
  const employeeDeleteAvatar = useEmployeeDeleteAvatar();
  const employeeSendInviteConfirmEmail = useEmployeeSendInviteConfirmEmail();
  const employeeCreateInviteLink = useEmployeeCreateInviteLink();
  const employeeUpdateBan = useEmployeeUpdateBan();
  const impersonationStart = useImpersonationStart();

  const { data: portalAccess } = useEmployeePortalAccess(
    employee.id,
    canViewField(employee, EmployeeFieldPermission.employee_portal_access)
  );
  const isPortalRestricted = !!portalAccess && portalAccess.mode !== PortalAccessMode.full;

  const canEditEmployee =
    canEditAnyOfFields(employee, [
      EmployeeFieldPermission.employee_name,
      EmployeeFieldPermission.employee_email,
      EmployeeFieldPermission.employee_date_birth,
      EmployeeFieldPermission.employee_gender,
      EmployeeFieldPermission.employee_phone_personal,
      EmployeeFieldPermission.employee_phone,
      EmployeeFieldPermission.employee_email_personal,

      EmployeeFieldPermission.employee_number,
      EmployeeFieldPermission.employee_address_address_line_1,
      EmployeeFieldPermission.employee_address_address_line_2,
      EmployeeFieldPermission.employee_address_country,
      EmployeeFieldPermission.employee_address_state,
      EmployeeFieldPermission.employee_address_city,
      EmployeeFieldPermission.employee_address_zip_code,
      EmployeeFieldPermission.employee_name_pronunciation,
      EmployeeFieldPermission.employee_timezone,
    ]) || employee.custom_fields.some((field) => field.permission_value === PermissionType.edit);

  const handleEditEmployee = useCallback(
    (employee: ApiEmployee) => {
      openModal(EmployeeEditModal, {
        employee: employee,
      });
    },
    [employee]
  );

  const handleEditBackground = useCallback(() => {
    openModal(ImageUploadModal, {
      modalTitle: t("common:background"),
      path: ApiFilePathTypes.employee,
      onSave: async (file: ApiFile) => {
        try {
          await employeeSetBackground.mutateAsync({
            id: employee.id,
            fileUUID: file.uuid,
          });

          analyticsService.trackEvent(analyticEvents.employees.edited, {
            [analyticProperties.id]: employee.id,
            [analyticProperties.actionType]: "Background Updated",
          });
          return true;
        } catch {
          return false;
        }
      },
    });
  }, [employee]);

  const handleDeleteBackground = async () => {
    try {
      await employeeDeleteBackground.mutateAsync(employee.id);

      analyticsService.trackEvent(analyticEvents.employees.edited, {
        [analyticProperties.actionType]: "Background Deleted",
      });
    } catch {
      return;
    }
  };

  const handleDelete2FA = async () => {
    try {
      await employeeDisableTwoFactor.mutateAsync(employee.id);

      analyticsService.trackEvent(analyticEvents.employees.edited, {
        [analyticProperties.actionType]: "2FA Disabled",
      });
    } catch {
      return;
    }
  };

  const handleAvatarUpload = useCallback(() => {
    openModal(ImageUploadModal, {
      withCrop: true,
      minImageSize: 256,
      modalTitle: t("common:photo"),
      entityId: employee.id,
      path: ApiFilePathTypes.employee_avatar,
      onSave: async (file: ApiFile) => {
        try {
          await employeeSetAvatar.mutateAsync({
            id: employee.id,
            fileUUID: file.uuid,
          });

          analyticsService.trackEvent(analyticEvents.employees.edited, {
            [analyticProperties.id]: employee.id,
            [analyticProperties.actionType]: "Photo Updated",
          });

          return true;
        } catch {
          return false;
        }
      },
    });
  }, [employee]);

  const handleAvatarDelete = async () => {
    try {
      await employeeDeleteAvatar.mutateAsync(employee.id);

      analyticsService.trackEvent(analyticEvents.organization.settingsEdited, {
        [analyticProperties.actionType]: "Photo Deleted",
      });
    } catch {
      return;
    }
  };

  const sendInvite = useCallback(
    async (inviteEmailType: ApiInviteEmailType) => {
      const success = await employeeSendInviteConfirmEmail.mutateAsync({
        employeeId: employee.id,
        inviteEmailType,
      });

      if (success) {
        showInfo({
          title: t("core:new_employee_invitation_sent", {
            email: getEmployeeInviteEmail(employee, inviteEmailType),
          }),
        });
      }
    },
    [employee, employeeSendInviteConfirmEmail]
  );

  const handleSendInvite = useCallback(() => {
    const inviteEmailTypes = getEmployeeInviteEmailTypes(employee);
    if (inviteEmailTypes.length === 2) {
      openModal(EmployeeInviteEmailModal, {
        workEmail: employee.email!,
        personalEmail: employee.email_personal!,
        onSelect: sendInvite,
      });
      return;
    }

    const inviteEmailType = inviteEmailTypes[0];
    if (inviteEmailType) sendInvite(inviteEmailType);
  }, [employee, openModal, sendInvite]);

  const [inviteLink, setInviteLink] = useState<
    { value: string; inviteEmailType: ApiInviteEmailType } | undefined
  >(undefined);
  const handleCopyInviteLink = useCallback(
    async (inviteEmailType: ApiInviteEmailType) => {
      let invitationLink =
        inviteLink?.inviteEmailType === inviteEmailType ? inviteLink.value : undefined;
      if (!invitationLink) {
        const employeeInviteLink = await employeeCreateInviteLink.mutateAsync({
          employeeId: employee.id,
          inviteEmailType,
        });
        invitationLink = employeeInviteLink.invite_link;
      }

      if (!invitationLink) return;

      copyTextWithNotify(invitationLink.trim()).then((copied) => {
        if (!copied) return;
        setInviteLink({ value: invitationLink, inviteEmailType });
        showInfo({
          title: t("core:invitation_link_copied_to_clipboard"),
        });
      });
    },
    [employee, employeeCreateInviteLink, inviteLink]
  );

  const handleSelectInviteLinkEmail = useCallback(() => {
    const inviteEmailTypes = getEmployeeInviteEmailTypes(employee);
    if (inviteEmailTypes.length === 2) {
      openModal(EmployeeInviteEmailModal, {
        workEmail: employee.email!,
        personalEmail: employee.email_personal!,
        modalTitle: t("core:copy_invitation_link"),
        actionTitle: t("core:copy_invitation_link"),
        description: t("core:copy_invitation_link_email_description"),
        onSelect: handleCopyInviteLink,
      });
      return;
    }

    const inviteEmailType = inviteEmailTypes[0];
    if (inviteEmailType) handleCopyInviteLink(inviteEmailType);
  }, [employee, handleCopyInviteLink]);

  const handleToggleAccess = useCallback(() => {
    openModal(ConfirmationModal, {
      question: employee.is_banned ? t("core:enable_access") : t("core:disable_access"),
      text: employee.is_banned ? t("core:enable_access_confirm") : t("core:disable_access_confirm"),
      acceptTitle: employee.is_banned ? t("common:enable") : t("common:disable"),
      onAccept: async () => {
        const isBanned = !employee.is_banned;

        try {
          await employeeUpdateBan.mutateAsync({
            employeeId: employee.id,
            payload: { is_banned: isBanned },
          });

          if (isBanned) {
            analyticsService.trackEvent(analyticEvents.employees.banned, {
              [analyticProperties.id]: employee.id,
            });
          } else {
            analyticsService.trackEvent(analyticEvents.employees.unbanned, {
              [analyticProperties.id]: employee.id,
            });
          }
        } catch {
          return;
        }
      },
    });
  }, [employee, employeeUpdateBan]);

  const handleDeleteEmployee = useCallback(() => {
    openModal(EmployeeDeleteModal, { employee });
  }, [employee, openModal]);

  const handleEmployeeTermination = useCallback(() => {
    openModal(EmployeeTerminationEditModal, { employee });
  }, [employee, openModal]);

  const handleImpersonate = useCallback(async () => {
    try {
      const result = await impersonationStart.mutateAsync({ employeeId: employee.id });
      window.open(`/impersonate?token=${encodeURIComponent(result.access_token)}`, "_blank");
    } catch {
      return;
    }
  }, [employee, impersonationStart]);

  const editMenu = useMemo(() => {
    const result = [];

    if (employee.id == employeeMe?.id) {
      result.push(
        <DropdownMenuItem
          onClick={() => navigate("/account-settings")}
          title={t("core:edit_manage_profile_menu_title")}
          description={t("core:manage_profile_caption")}
          key={"accounts-settings"}
        />
      );
    }

    const editProfileMenuItems = [];

    if (canEditEmployee) {
      editProfileMenuItems.push(
        <DropdownMenuItem
          onClick={() => handleEditEmployee(employee)}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-pen-to-square" />}
          title={t("core:edit_employee_menu_title")}
          description={t("core:edit_employee_caption")}
        />
      );
    }

    if (canEditField(employee, EmployeeFieldPermission.employee_avatar)) {
      editProfileMenuItems.push(
        <DropdownMenuItem
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-image"} />}
          onClick={handleAvatarUpload}
          title={t("core:upload_photo")}
          description={t("core:upload_photo_caption")}
        />
      );
      editProfileMenuItems.push(
        <DropdownMenuItem
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          onClick={handleAvatarDelete}
          title={t("core:delete_photo")}
          description={t("core:delete_photo_caption")}
        />
      );
    }
    if (canEditField(employee, EmployeeFieldPermission.employee_background)) {
      editProfileMenuItems.push(
        <DropdownMenuItem
          onClick={handleEditBackground}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-image" />}
          title={t("core:change_background")}
          description={t("core:change_background_caption")}
        />
      );
    }
    if (
      canEditField(employee, EmployeeFieldPermission.employee_background) &&
      employee?.background_url
    ) {
      editProfileMenuItems.push(
        <DropdownMenuItem
          onClick={handleDeleteBackground}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
          title={t("core:remove_background")}
          description={t("core:remove_background_caption")}
        />
      );
    }

    if (editProfileMenuItems.length > 0) {
      result.push(
        <DropdownMenuSub
          title={t("core:edit_employee")}
          description={t("core:edit_employee_sub_caption")}
        >
          {editProfileMenuItems}
        </DropdownMenuSub>
      );
    }

    const changeWorkDetails = [];
    if (canEditField(employee, EmployeeFieldPermission.manage_employee_position)) {
      changeWorkDetails.push(
        <DropdownMenuItem
          leftIcon={<FontAwesomeIcon icon="fa-light fa-id-badge" />}
          title={t("core:change_position")}
          description={t("core:change_position_caption")}
          onClick={() => {
            openModal(EmployeePositionEditModal, {
              employee,
              employeePosition: undefined,
            });
          }}
        />
      );
    }
    if (canEditField(employee, EmployeeFieldPermission.manage_employee_employment)) {
      changeWorkDetails.push(
        <DropdownMenuItem
          leftIcon={<FontAwesomeIcon icon="fa-light fa-file-contract" />}
          title={t("core:change_employment")}
          description={t("core:change_employment_caption")}
          onClick={() => {
            openModal(EmployeeEmploymentEditModal, {
              employee,
              employeeEmployment: undefined,
            });
          }}
        />
      );
    }

    if (canEditField(employee, EmployeeFieldPermission.manage_employee_compensation)) {
      changeWorkDetails.push(
        <DropdownMenuItem
          leftIcon={<FontAwesomeIcon icon="fa-light fa-coins" />}
          title={t("core:change_compensation")}
          description={t("core:change_compensation_caption")}
          onClick={() => {
            openModal(EmployeeCompensationEditModal, {
              employee,
              employeeCompensation: undefined,
            });
          }}
        />
      );
    }

    if (
      canEditField(employee, EmployeeFieldPermission.manage_employee_termination) &&
      !employee.employee_termination
    ) {
      changeWorkDetails.push(
        <DropdownMenuItem
          variant={"menuDanger"}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-person-through-window" />}
          title={t("core:terminate_employee")}
          description={t("core:terminate_employee_menu_caption")}
          onClick={handleEmployeeTermination}
        />
      );
    }

    if (changeWorkDetails.length > 0) {
      result.push(
        <DropdownMenuSub
          title={t("core:change_work_details")}
          description={t("core:change_work_details_caption")}
        >
          {changeWorkDetails}
        </DropdownMenuSub>
      );
    }

    if (
      canIEdit(ApiPermission.workflows_manual_run) &&
      isFeatureEnabled(ApiFeatureType.workflows)
    ) {
      result.push(
        <DropdownMenuSub
          title={t("workflow:assign_workflow")}
          description={t("workflow:assign_workflow_caption")}
        >
          <DropdownMenuItem
            onClick={() => {
              openModal(WorkflowAssignToEmployeeModal, {
                employeeId: employee.id,
              });
            }}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-diagram-project" />}
            title={t("workflow:assign_workflow")}
            description={t("workflow:assign_workflow_caption")}
          />
        </DropdownMenuSub>
      );
    }

    const manageAcccess = [];
    const canSendInvite = canSendEmployeeInvite(employee, canIEdit(ApiPermission.add_employees));
    const canCreateInviteLink = canCreateEmployeeInviteLink(
      employee,
      canIEdit(ApiPermission.add_employees)
    );
    if (canIEdit(ApiPermission.add_employees)) {
      if (canSendInvite) {
        manageAcccess.push(
          <DropdownMenuItem
            onClick={handleSendInvite}
            leftIcon={<FontAwesomeIcon icon="fa-light fa-envelope" />}
            title={t("core:send_invitation")}
            description={t("core:send_invitation_caption")}
          />
        );
      }
      if (canCreateInviteLink) {
        manageAcccess.push(
          <DropdownMenuItem
            onClick={handleSelectInviteLinkEmail}
            leftIcon={
              <FontAwesomeIcon icon={twMerge("fa-light", inviteLink ? "fa-check" : "fa-copy")} />
            }
            title={inviteLink ? t("common:link_copied") : t("core:copy_invitation_link")}
            description={t("core:copy_invitation_link_caption")}
          />
        );
      }
    }

    if (isPortalRestricted) {
      manageAcccess.push(
        <DropdownMenuItem
          onClick={() => openModal(EmployeePortalAccessModal, { employee })}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-door-closed" />}
          title={t("core:portal_access")}
          description={t("core:portal_access_menu_caption")}
        />
      );
    }

    if (canIEdit(ApiPermission.impersonate_employees) && employee.id !== employeeMe?.id) {
      manageAcccess.push(
        <DropdownMenuItem
          onClick={handleImpersonate}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-arrow-right-to-bracket" />}
          title={t("core:impersonate_employee")}
          description={t("core:impersonate_employee_caption")}
        />
      );
    }

    if (canIEdit(ApiPermission.add_employees) && employee.tfa_enabled) {
      manageAcccess.push(
        <DropdownMenuItem
          variant={"menuDanger"}
          onClick={handleDelete2FA}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-ban" />}
          title={t("core:disable_2fa")}
          description={t("core:disable_2fa_caption")}
        />
      );
    }
    if (canIEdit(ApiPermission.ban_employees) && employee.id !== employeeMe?.id) {
      manageAcccess.push(
        <DropdownMenuItem
          variant={employee.is_banned ? undefined : "menuDanger"}
          onClick={handleToggleAccess}
          leftIcon={
            <FontAwesomeIcon
              icon={employee.is_banned ? "fa-light fa-lock-open" : "fa-light fa-lock"}
            />
          }
          title={employee.is_banned ? t("core:enable_access") : t("core:disable_access")}
          description={
            employee.is_banned ? t("core:enable_access_caption") : t("core:disable_access_caption")
          }
        />
      );
    }

    if (canIEdit(ApiPermission.delete_employees) && employee.id !== employeeMe?.id) {
      manageAcccess.push(
        <DropdownMenuItem
          variant={"menuDanger"}
          onClick={handleDeleteEmployee}
          leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />}
          title={t("core:delete_employee")}
          description={t("core:delete_employee_caption")}
        />
      );
    }

    if (manageAcccess.length > 0) {
      result.push(
        <DropdownMenuSub
          title={t("core:manage_access")}
          description={t("core:manage_access_caption")}
        >
          {manageAcccess}
        </DropdownMenuSub>
      );
    }

    return result;
  }, [employee, isPortalRestricted]);

  return (
    <>
      {editMenu.length > 0 && (
        <DropdownMenu
          trigger={
            <Button
              variant={"secondary"}
              rightIcon={<FontAwesomeIcon icon={"fa-light" + " fa-chevron-down"} />}
            >
              {t("common:actions")}
            </Button>
          }
        >
          {editMenu}
        </DropdownMenu>
      )}
    </>
  );
};
